import type { ReactNode } from 'react';
import { useTranslation } from '@/store/languageStore';

type LoginLayoutProps = {
  children: ReactNode;
};

function WordmarkIcon() {
  return (
    <svg viewBox="0 0 100 100" className="h-10 w-10 shrink-0" aria-hidden>
      <defs>
        <radialGradient id="loginGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#1FE2BC" stopOpacity="0.28" />
          <stop offset="1" stopColor="#1FE2BC" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" rx="24" fill="#1F2424" />
      <circle cx="50" cy="50" r="30" fill="url(#loginGlow)" />
      <circle cx="50" cy="50" r="36" fill="none" stroke="#1FE2BC" strokeWidth="4.5" />
      <path
        d="M48.5 24.5 A25.5 25.5 0 1 1 48.5 75.5"
        fill="none"
        stroke="#1FE2BC"
        strokeWidth="6.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

const LoginLayout = ({ children }: LoginLayoutProps) => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <div
      className="grid min-h-screen bg-[#F6F8F8] text-[#10242A] max-[860px]:grid-cols-1 min-[861px]:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]"
      style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}
    >
      <section className="relative flex min-h-[38vh] flex-col justify-between overflow-hidden bg-[radial-gradient(120%_90%_at_20%_10%,#12414A_0%,#0E2B33_45%,#081D23_100%)] px-6 py-8 text-white min-[861px]:min-h-screen min-[861px]:px-14 min-[861px]:py-12">
        <div className="relative z-[1] flex items-center gap-3 text-[26px] font-extrabold tracking-tight text-white">
          <WordmarkIcon />
          DQFleet
        </div>

        <div className="relative z-[1]">
          <h1 className="mb-[18px] max-w-[14ch] text-[30px] font-extrabold leading-[1.05] tracking-[-0.03em] min-[861px]:text-[clamp(34px,4vw,52px)]">
            {t('login.heroTitleLine1')} {t('login.heroTitleLine2')}
          </h1>
          <p className="hidden max-w-[44ch] text-[16px] leading-relaxed text-white/70 min-[861px]:block">
            {t('login.heroBody')}
          </p>
        </div>

        <footer className="relative z-[1] hidden text-[15px] text-white/45 min-[861px]:block">
          {t('login.brandFooter').replace('{year}', String(year))}
        </footer>

        <svg
          className="pointer-events-none absolute -right-[30%] top-1/2 h-[120vw] w-[120vw] -translate-y-1/2 opacity-55 min-[861px]:-right-[22%] min-[861px]:h-[min(88vh,800px)] min-[861px]:w-[min(88vh,800px)] min-[861px]:opacity-[0.32]"
          viewBox="0 0 100 100"
          aria-hidden
        >
          <defs>
            <radialGradient id="loginGlowL" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#1FE2BC" stopOpacity="0.16" />
              <stop offset="1" stopColor="#1FE2BC" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="30" fill="url(#loginGlowL)" />
          <circle cx="50" cy="50" r="36" fill="none" stroke="#1FE2BC" strokeWidth="4.5" opacity="0.85" />
          <path
            className="login-mark-sweep"
            d="M48.5 24.5 A25.5 25.5 0 1 1 48.5 75.5"
            fill="none"
            stroke="#1FE2BC"
            strokeWidth="6.5"
            strokeLinecap="round"
          />
        </svg>
      </section>

      <main className="flex items-center justify-center px-6 py-8 min-[861px]:px-8 min-[861px]:py-12">
        <div className="w-full max-w-[400px]">{children}</div>
      </main>
    </div>
  );
};

export default LoginLayout;
